import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { useInstallScript } from '../../hooks/useDevices';

export function InstallAppCard() {
  const [requested, setRequested] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError } = useInstallScript('win32', requested);

  const handleDownload = () => {
    if (!data?.script) return;
    const blob = new Blob([data.script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = data.filename || 'install-apace-pulse.ps1';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    // The one-liner (win32 only) runs via `-EncodedCommand`, which Windows
    // never treats as "running a script file" — so it's unaffected by the
    // RemoteSigned/Mark-of-the-Web block that stops the downloaded .ps1
    // from running directly (confirmed by hand: "the file is not digitally
    // signed" on a real Windows install, even via right-click > Run with
    // PowerShell). Falls back to the raw script for platforms without one.
    const textToCopy = data?.oneLinerCommand ?? data?.script;
    if (!textToCopy) return;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be unavailable (e.g. insecure context) — the
      // download button and visible script block below still work.
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Install Apace Pulse</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          Install the Apace Pulse agent on this computer to sync your attendance and work
          sessions automatically. It only takes one step.
        </p>

        {!requested && (
          <Button onClick={() => setRequested(true)}>Get Install Script</Button>
        )}

        {requested && isLoading && (
          <p className="text-sm text-gray-500">Generating your personal install script...</p>
        )}

        {requested && isError && (
          <p className="text-sm text-red-600">
            Couldn&apos;t generate an install script right now. Please try again.
          </p>
        )}

        {requested && data?.script && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleCopy}>{copied ? 'Copied!' : 'Copy Command'}</Button>
              <Button variant="outline" onClick={handleDownload}>
                Download {data.filename}
              </Button>
            </div>
            <ol className="text-xs text-gray-500 list-decimal list-inside space-y-1">
              <li>Click &quot;Copy Command&quot; above.</li>
              <li>
                Open PowerShell (Start menu → type &quot;PowerShell&quot; → Enter), paste, and press
                Enter.
              </li>
              <li>Apace Pulse installs, activates, and starts automatically — no further steps.</li>
            </ol>
            {data.oneLinerCommand && (
              <p className="text-xs text-gray-400">
                Downloading the .ps1 file and double-clicking it (or &quot;Run with PowerShell&quot;)
                won&apos;t work — Windows blocks unsigned scripts downloaded from a browser. The
                copied command above sidesteps that entirely; the download below is only for
                inspecting the script or advanced manual use.
              </p>
            )}
            <pre className="text-xs bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto max-h-48 whitespace-pre-wrap">
              {data.script}
            </pre>
            <p className="text-xs text-gray-400">
              This script is personal to you and expires soon — generate a new one if it doesn&apos;t
              get used in time.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
